import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getV1CkycDataByUserId } from "../../../actions/user.action";
import Box from "../../../components/box";
import Button from "../../../components/button";
import CustomAlert from "../../../components/custom-alert";
import { CkycDataDto, CkycDocDto } from "../../../dto/ckyc.dto";
import { ReactComponent as EyeIcon } from "../../../images/eye.svg";
import dispatch from "../../../middleware";
import { FileType } from "../../../models";
import { ReduxState } from "../../../reducers";

const docKeys = [
  "aadhaarDoc",
  "panDoc",
  "voterIdDoc",
  "drivingLicenseDoc",
  "photographDoc",
];
const DOC_KEYS_WITH_LABEL = [
  {
    key: "aadhaarDoc",
    label: "Aadhaar :",
  },
  {
    key: "panDoc",
    label: "PAN :",
  },
  {
    key: "voterIdDoc",
    label: "Voter ID :",
  },
  {
    key: "drivingLicenseDoc",
    label: "DL :",
  },
  {
    key: "photographDoc",
    label: "Photo :",
  },
];

interface CkycDocsProps {
  onShowDocumentClick: (doc?: { body: string; type: FileType }) => void;
  userId: string;
}

export default function CkycDocs({
  onShowDocumentClick,
  userId,
}: CkycDocsProps) {
  const storeDispatch = useDispatch();
  const ckycDetails: CkycDataDto | null = useSelector(
    (state: ReduxState) => state.user.ckycDetailsV1
  );

  const getDocumentData = useCallback(
    (docKeyName: string): { body: string; type: FileType } | null => {
      if (!docKeys.includes(docKeyName) || !ckycDetails) return null;
      const doc = ckycDetails[docKeyName as keyof CkycDataDto] as CkycDocDto;
      if (!doc) return null;
      return {
        body: doc.b64Image,
        type: (doc.extension ?? FileType.IMG_JPEG) as FileType,
      };
    },
    [ckycDetails]
  );

  const hasAnyCkycDoc = useMemo(
    () => DOC_KEYS_WITH_LABEL.some(({ key }) => !!getDocumentData(key)),
    [getDocumentData]
  );

  useEffect(() => {
    if (!userId) return;
    dispatch(storeDispatch, getV1CkycDataByUserId(userId));
  }, [storeDispatch, userId]);

  if (!ckycDetails) return null;

  return (
    <div className="dashboard-container p-3">
      <Box header={<h3 className="user-page-title">CKYC Documents</h3>}>
        {!hasAnyCkycDoc && (
          <CustomAlert variant="warning">No documents available</CustomAlert>
        )}
        {hasAnyCkycDoc &&
          DOC_KEYS_WITH_LABEL.map(({ key, label }) => {
            const documentData = getDocumentData(key);
            if (!documentData) return null;
            return (
              <div className="row align-items-center my-4" key={key}>
                <div className="col-2">{label}</div>
                <div className="col">
                  <Button
                    variant="info"
                    title="View Document"
                    onClick={() => onShowDocumentClick(documentData)}
                  >
                    <EyeIcon />
                    View
                  </Button>
                  {/* RBI AUDIT: Document must be previewed in-portal instead of
                      downloaded to the user's device. Original download anchor
                      is commented out below (not removed) for audit trail.
                  <a
                    className="btn rf-btn rf-btn-info rf-btn-sm"
                    href={getDataUrlFromDocTypeAndBody(
                      documentData.type,
                      documentData.body
                    )}
                    title="Download"
                    download={key}
                  >
                    <DownloadIcon color="var(--font-color)" />
                  </a>
                  */}
                </div>
              </div>
            );
          })}
        <div className="row align-items-center my-4">
          <div className="col-2">Metadata</div>
          <div className="col">
            <Button
              variant="info"
              title="View Document"
              onClick={() =>
                onShowDocumentClick({
                  body: JSON.stringify(ckycDetails),
                  type: FileType.APP_JSON,
                })
              }
            >
              <EyeIcon />
              View
            </Button>
          </div>
        </div>
      </Box>
    </div>
  );
}
